import { useEffect, useState } from "react";
import { Plus, Pencil, Trash2, ArrowUp, ArrowDown, X, Loader2, ImageIcon, RefreshCw, Zap } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { useSite } from "@/lib/site";
import { AssetPicker } from "./AssetPicker";

const Field = ({ f, value, onChange, testid }) => {
  const [pickerOpen, setPickerOpen] = useState(false);
  const base = "w-full rounded-xl border border-ink/15 bg-white px-4 py-2.5 text-sm outline-none focus:border-terra";
  if (f.type === "textarea")
    return <textarea data-testid={testid} rows={3} value={value || ""} onChange={(e) => onChange(e.target.value)} className={base} placeholder={f.label} />;
  if (f.type === "checkbox")
    return (
      <label className="flex items-center gap-2 text-sm text-plumsoft">
        <input data-testid={testid} type="checkbox" checked={!!value} onChange={(e) => onChange(e.target.checked)} className="h-4 w-4 accent-terra" />
        {f.label}
      </label>
    );
  if (f.type === "image")
    return (
      <div className="sm:col-span-2">
        <div className="flex gap-2">
          <input data-testid={testid} value={value || ""} onChange={(e) => onChange(e.target.value)} className={base} placeholder={`${f.label} — paste URL or pick from library`} />
          <button
            type="button"
            data-testid={`${testid}-library`}
            onClick={() => setPickerOpen(true)}
            className="inline-flex shrink-0 items-center gap-1.5 rounded-xl border border-ink/15 bg-white px-4 text-xs font-semibold text-ink hover:border-terra hover:text-terra"
          >
            <ImageIcon className="h-4 w-4" /> Library
          </button>
        </div>
        {value && <img src={value} alt="" className="mt-2 h-16 w-16 rounded-lg object-cover" />}
        <AssetPicker open={pickerOpen} onClose={() => setPickerOpen(false)} onSelect={onChange} />
      </div>
    );
  return (
    <input data-testid={testid} type={f.type || "text"} value={value || ""} onChange={(e) => onChange(f.type === "number" ? Number(e.target.value) : e.target.value)} className={base} placeholder={f.label} />
  );
};

export const ContentTab = ({ coll, fields, titleKey = "title", subtitleKey, syncable = false }) => {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const { refresh } = useSite();

  const load = () => api.get(`/admin/content/${coll}`).then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [coll]);

  const sync = async () => {
    setSyncing(true);
    try {
      const r = await api.post("/admin/propract/sync");
      toast.success(`Synced from ProPract — ${r.data.services} services, ${r.data.courses} courses`);
      load();
      refresh();
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(formatApiError(d?.message || d));
    } finally {
      setSyncing(false);
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      if (editing.id) await api.put(`/admin/content/${coll}/${editing.id}`, editing);
      else await api.post(`/admin/content/${coll}`, editing);
      toast.success("Saved");
      setEditing(null);
      load();
      refresh();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this item?")) return;
    await api.delete(`/admin/content/${coll}/${id}`);
    toast.success("Deleted");
    load();
    refresh();
  };

  const move = async (index, dir) => {
    const next = [...items];
    const j = index + dir;
    if (j < 0 || j >= next.length) return;
    [next[index], next[j]] = [next[j], next[index]];
    setItems(next);
    await api.put(`/admin/content/${coll}/reorder`, { ids: next.map((i) => i.id) });
    refresh();
  };

  return (
    <div data-testid={`admin-tab-${coll}`}>
      <div className="mb-5 flex items-center justify-between">
        <div>
          <h2 className="font-display text-2xl font-semibold capitalize text-ink">{coll}</h2>
          {syncable && (
            <p className="mt-1 text-xs text-faint">
              Items marked <span className="inline-flex items-center gap-1 rounded-full bg-terra/10 px-2 py-0.5 text-[10px] font-semibold text-terra"><Zap className="h-2.5 w-2.5" />ProPract</span> are live from ProPract — sync updates their core details; your Why/Who blocks are preserved.
            </p>
          )}
        </div>
        <div className="flex items-center gap-2">
          {syncable && (
            <button
              data-testid={`admin-${coll}-sync`}
              onClick={sync}
              disabled={syncing}
              className="inline-flex items-center gap-1.5 rounded-full border border-terra/30 bg-terra/5 px-4 py-2 font-outfit text-xs font-semibold text-terra hover:bg-terra/15 disabled:opacity-60"
            >
              {syncing ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />}
              Sync from ProPract
            </button>
          )}
          <button
            data-testid={`admin-${coll}-add`}
            onClick={() => setEditing({})}
            className="inline-flex items-center gap-1.5 rounded-full bg-ink px-4 py-2 font-outfit text-xs font-semibold text-sand hover:bg-terra"
          >
            <Plus className="h-3.5 w-3.5" /> Add new
          </button>
        </div>
      </div>

      {editing && (
        <div className="mb-6 rounded-2xl border border-terra/30 bg-white p-6 shadow-lift" data-testid={`admin-${coll}-form`}>
          <div className="mb-4 flex items-center justify-between">
            <p className="font-outfit text-sm font-semibold text-ink">{editing.id ? "Edit item" : "New item"}</p>
            <button data-testid={`admin-${coll}-form-close`} onClick={() => setEditing(null)} aria-label="Close form">
              <X className="h-4 w-4 text-faint" />
            </button>
          </div>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            {fields.map((f) => (
              <div key={f.name} className={f.type === "textarea" ? "sm:col-span-2" : ""}>
                <Field
                  f={f}
                  testid={`admin-${coll}-field-${f.name}`}
                  value={editing[f.name]}
                  onChange={(v) => setEditing({ ...editing, [f.name]: v })}
                />
              </div>
            ))}
          </div>
          <button
            data-testid={`admin-${coll}-save`}
            onClick={save}
            disabled={saving}
            className="mt-4 inline-flex items-center gap-2 rounded-full bg-terra px-5 py-2.5 font-outfit text-sm font-semibold text-white hover:bg-terradark disabled:opacity-60"
          >
            {saving && <Loader2 className="h-4 w-4 animate-spin" />} Save
          </button>
        </div>
      )}

      <div className="space-y-2">
        {items.map((item, i) => (
          <div
            key={item.id}
            data-testid={`admin-${coll}-row-${i}`}
            className="flex items-center gap-3 rounded-xl border border-ink/10 bg-white px-4 py-3"
          >
            <div className="flex flex-col">
              <button data-testid={`admin-${coll}-up-${i}`} onClick={() => move(i, -1)} disabled={i === 0} aria-label="Move up" className="text-faint hover:text-terra disabled:opacity-30">
                <ArrowUp className="h-3.5 w-3.5" />
              </button>
              <button data-testid={`admin-${coll}-down-${i}`} onClick={() => move(i, 1)} disabled={i === items.length - 1} aria-label="Move down" className="text-faint hover:text-terra disabled:opacity-30">
                <ArrowDown className="h-3.5 w-3.5" />
              </button>
            </div>
            <div className="min-w-0 flex-1">
              <p className="flex items-center gap-2 truncate text-sm font-medium text-ink">
                {item[titleKey] || item.label || item.name || item.value}
                {item.pp_id && (
                  <span className="inline-flex shrink-0 items-center gap-1 rounded-full bg-terra/10 px-2 py-0.5 text-[10px] font-outfit font-semibold text-terra">
                    <Zap className="h-2.5 w-2.5" /> ProPract
                  </span>
                )}
              </p>
              {subtitleKey && <p className="truncate text-xs text-faint">{item[subtitleKey]}</p>}
            </div>
            <button data-testid={`admin-${coll}-edit-${i}`} onClick={() => setEditing(item)} aria-label="Edit" className="rounded-lg p-2 text-faint hover:bg-cream hover:text-terra">
              <Pencil className="h-4 w-4" />
            </button>
            <button data-testid={`admin-${coll}-delete-${i}`} onClick={() => remove(item.id)} aria-label="Delete" className="rounded-lg p-2 text-faint hover:bg-red-50 hover:text-red-500">
              <Trash2 className="h-4 w-4" />
            </button>
          </div>
        ))}
        {items.length === 0 && <p className="py-8 text-center text-sm text-faint">No items yet — add the first one.</p>}
      </div>
    </div>
  );
};
